import React, { useState, useEffect, useRef } from 'react';
import Layout from '../component/layout';
import Select from 'react-select';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Link, useNavigate } from 'react-router-dom';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage'
import { makeRequest2 } from '../../../utils/makeRequest';
import { addCampaignURL, getCustomersURL, getFacilityUnits } from '../../../utils/urls';

const DEFAULT_LIMIT = 1000;
const SMS_LIMIT = 1000;

const AddCampaign = () => {
    const editorRef = useRef(null);
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [objective, setObjective] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [targetAudience, setTargetAudience] = useState('');
    const [channels, setChannels] = useState([]);
    const [message, setMessage] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [campaignType, setCampaignType] = useState('immediate');
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [scheduledDates, setScheduledDates] = useState([{ date: '', time: '' }]);
    const [emailSubject, setEmailSubject] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [landlordType, setLandlordType] = useState('all');

    const [divisions, setDivisions] = useState([]);
    const [selectedDivisions, setSelectedDivisions] = useState([]);

    const quillRef = useRef(null);

    // const maxLength = channels.includes('SMS') ? SMS_LIMIT : DEFAULT_LIMIT;
    const maxLength = (channels.includes('SMS') || channels.includes('WhatsApp')) ? SMS_LIMIT : DEFAULT_LIMIT;

    const handleEditorChange = (content, editor) => {
        // Remove HTML tags to count plain text characters
        const plainText = content.replace(/<[^>]*>/g, '');

        if (plainText.length <= maxLength) {
            setMessage(content);
        } else {
            // Prevent exceeding character limit
            const truncatedText = plainText.substring(0, maxLength);
            // You might want to show a warning here
            console.warn('Character limit exceeded');
        }
    };

    const getPlainTextLength = () => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return 0;
        // Quill's getText() always ends with a trailing '\n' — subtract 1
        return editor.getText().length - 1;
    };

    const stripHtmlTags = (html) => {
        // Create a temporary div element
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Get text content and clean up extra whitespace
        return tempDiv.textContent || tempDiv.innerText || '';
    };

    const handleChannelChange = (channel) => {
        setChannels((prevChannels) =>
            prevChannels.includes(channel)
                ? prevChannels.filter((c) => c !== channel) // Remove if already selected
                : [...prevChannels, channel] // Add if not selected
        );
    };

    const handleScheduledDateChange = (index, field, value) => {
        const updatedDates = scheduledDates.map((dateTime, i) =>
            i === index ? { ...dateTime, [field]: value } : dateTime
        );
        setScheduledDates(updatedDates);
    };

    const addScheduledDate = () => {
        setScheduledDates([...scheduledDates, { date: '', time: '' }]);
    };

    const removeScheduledDate = (index) => {
        if (scheduledDates.length > 1) {
            const updatedDates = scheduledDates.filter((_, i) => i !== index);
            setScheduledDates(updatedDates);
        }
    };

    const fetchCustomersForFacility = async () => {
        try {
            const value = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getCustomersURL}/${value}`, 'GET', {});
            console.log("response", response)
            if (response.success) {
                // Sort customers by createdAt in descending order
                const sortedCustomers = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setCustomers(sortedCustomers);
            } else {
                throw new Error('Failed to fetch customers');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        }
    };

    const fetchDivisions = async () => {
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getFacilityUnits}/${facilityId}`, 'GET', {});
            if (response.success) {
                const units = response.data.units;
                // Extract unique non-empty divisions
                const uniqueDivisions = [...new Set(
                    units
                        .map(u => u.division)
                        .filter(d => d && d.trim() !== '')
                )].sort();
                setDivisions(uniqueDivisions);
            } else {
                throw new Error('Failed to fetch units');
            }
        } catch (err) {
            toastify(err.message, 'error');
        }
    };

    const customerOptions = customers.map(c => ({
        value: c._id,
        label: `${c.firstName} ${c.lastName}`,
    }));

    const sanitizeForSms = (text) => {
        return text
            .replace(/\u00A0/g, ' ')          // non-breaking space -> normal space
            .replace(/[\u2018\u2019]/g, "'")  // curly single quotes
            .replace(/[\u201C\u201D]/g, '"')  // curly double quotes
            .replace(/[\u2013\u2014]/g, '-')  // en/em dash
            .replace(/\u2026/g, '...')        // ellipsis
            .replace(/m\u00B2/g, 'sq m')      // m²
            .replace(/m\u00B3/g, 'cu m');     // m³
    };


    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);

            // Get plain text version for sending
            const plainTextMessage = stripHtmlTags(message);
            const smsSafeMessage = channels.includes('SMS') ? sanitizeForSms(plainTextMessage) : plainTextMessage;

            const facilityId = await getItem('selectedFacilityId');

            if (channels.includes('Email') && !emailSubject) {
                toastify('Email subject is required when email channel is selected', 'error');
                return
            }

            if (campaignType === 'scheduled' && (!scheduleDate || !scheduleTime)) {
                toastify('Please select both schedule date and time', 'error');
                return
            }

            if (campaignType === 'recurring') {
                const invalidDates = scheduledDates.some(dt => !dt.date || !dt.time);
                if (invalidDates) {
                    toastify('Please fill in all scheduled dates and times', 'error');
                    return
                }
            }

            if (targetAudience === 'individuals' && !selectedUser) {
                toastify('Please select a user for individual campaigns', 'error');
                return
            }

            if (targetAudience === 'division' && selectedDivisions.length === 0) {
                toastify('Please select at least one block/division', 'error');
                return;
            }

            if (!name || !objective || !description || !targetAudience || !channels || !message) {
                toastify('Please fill in all fields', 'error');
                return
            }

            const campaignData = {
                name,
                objective,
                description,
                startDate,
                endDate,
                targetAudience,
                channels,
                message: smsSafeMessage,
                emailSubject,
                campaignType,
                ...(channels.includes('Email') && { htmlMessage: message }),
                // ✅ Include selected user only if targeting individuals
                ...(targetAudience === 'individuals' && selectedUser && { individualUserId: selectedUser.value }),
                ...(targetAudience === 'division' && { selectedDivisions: selectedDivisions.map(d => d.value) }),
                ...(targetAudience === 'landlords' && { landlordType }),
            }

            console.log('Campaign data:', campaignData);
            console.log('Plain text message:', smsSafeMessage);
            console.log('HTML message:', message);

            if (campaignType === 'scheduled') {
                campaignData.scheduleDate = scheduleDate;
                campaignData.scheduleTime = scheduleTime;
            } else if (campaignType === 'recurring') {
                campaignData.scheduledDates = scheduledDates;
            }

            if (targetAudience === 'individuals' && selectedUser) {
                campaignData.individualUserId = selectedUser.value;
            }

            const response = await makeRequest2(`${addCampaignURL}/${facilityId}`, 'POST', campaignData);

            if (response.success) {
                toastify('Campaign added successfully', 'success');
                handleClear();
                navigate('/facility/campaigns');
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            // toastify(err.message, 'error');
            throw new Error(err.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClear = () => {
        setName('');
        setObjective('');
        setDescription('');
        setStartDate('');
        setEndDate('');
        setTargetAudience('');
        setMessage('');
        setChannels([]);
        setSelectedUser(null);
        setSelectedDivisions([]);
        setDivisions([]);
    }

    useEffect(() => {
        if (targetAudience === 'individuals') {
            fetchCustomersForFacility();
        } else if (targetAudience === 'division') {
            fetchDivisions();
        }
    }, [targetAudience]);


    return (
        <Layout>
            <div>
                <div className="card">
                    <div className="card-header">
                        <Link to={"#"} onClick={() => navigate(-1)}><span><i className="ti ti-arrow-narrow-left"></i> Back</span></Link>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">


                        <div className="row">

                            <div className="col-sm-12">
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Add Campaign</h5>
                                    </div>

                                    <div className='card-body'>

                                        <div className="row">

                                            <div className="col-lg-12 col-xxl-12">
                                                <div className="card">

                                                    <div className="card-body position-relative">

                                                        <div className="container mt-4">
                                                            <div className="mb-3">
                                                                <label className="form-label">Campaign Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="name"
                                                                    value={name}
                                                                    onChange={(e) => setName(e.target.value)}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="mb-3">
                                                                <label className="form-label">Objective</label>
                                                                <select
                                                                    className="form-select"
                                                                    name="objective"
                                                                    value={objective}
                                                                    onChange={(e) => setObjective(e.target.value)}
                                                                    required
                                                                >
                                                                    <option value="">Select Objective</option>
                                                                    <option value="Brand Awareness">Brand Awareness / Notifications</option>
                                                                    <option value="Sales">Sales / Marketing</option>
                                                                </select>
                                                            </div>

                                                            <div className="mb-3">
                                                                <label className="form-label">Description</label>
                                                                <textarea
                                                                    className="form-control"
                                                                    name="description"
                                                                    rows="4"
                                                                    value={description}
                                                                    onChange={(e) => setDescription(e.target.value)}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="mb-3">
                                                                <label className="form-label">Campaign Type</label>
                                                                <div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="campaignType"
                                                                            value="immediate"
                                                                            checked={campaignType === 'immediate'}
                                                                            onChange={(e) => setCampaignType(e.target.value)}
                                                                        />
                                                                        <label className="form-check-label">Send Immediately</label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="campaignType"
                                                                            value="scheduled"
                                                                            checked={campaignType === 'scheduled'}
                                                                            onChange={(e) => setCampaignType(e.target.value)}
                                                                        />
                                                                        <label className="form-check-label">Schedule Campaign</label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="campaignType"
                                                                            value="recurring"
                                                                            checked={campaignType === 'recurring'}
                                                                            onChange={(e) => setCampaignType(e.target.value)}
                                                                        />
                                                                        <label className="form-check-label">Multiple Dates</label>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {campaignType === 'scheduled' && (
                                                                <div className="mb-3">
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <label className="form-label">Schedule Date</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                name="scheduleDate"
                                                                                value={scheduleDate}
                                                                                onChange={(e) => setScheduleDate(e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <label className="form-label">Schedule Time</label>
                                                                            <input
                                                                                type="time"
                                                                                className="form-control"
                                                                                name="scheduleTime"
                                                                                value={scheduleTime}
                                                                                onChange={(e) => setScheduleTime(e.target.value)}
                                                                                required
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {campaignType === 'recurring' && (
                                                                <div className="mb-3">
                                                                    <label className="form-label">Campaign Dates & Times</label>
                                                                    {scheduledDates.map((dateTime, index) => (
                                                                        <div key={index} className="row mb-2">
                                                                            <div className="col-md-4">
                                                                                <input
                                                                                    type="date"
                                                                                    className="form-control"
                                                                                    value={dateTime.date}
                                                                                    onChange={(e) => handleScheduledDateChange(index, 'date', e.target.value)}
                                                                                    required
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-4">
                                                                                <input
                                                                                    type="time"
                                                                                    className="form-control"
                                                                                    value={dateTime.time}
                                                                                    onChange={(e) => handleScheduledDateChange(index, 'time', e.target.value)}
                                                                                    required
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-4">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-outline-danger btn-sm"
                                                                                    onClick={() => removeScheduledDate(index)}
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-outline-primary btn-sm"
                                                                        onClick={addScheduledDate}
                                                                    >
                                                                        + Add Date & Time
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <div className="mb-3">
                                                                <label className="form-label">Target Audience</label>
                                                                <select
                                                                    className="form-select"
                                                                    name="targetAudience"
                                                                    value={targetAudience}
                                                                    onChange={(e) => {
                                                                        setTargetAudience(e.target.value);
                                                                        setSelectedDivisions([]); // reset on change
                                                                        setLandlordType('all');
                                                                    }}
                                                                    required
                                                                >
                                                                    <option value="">Select Target Audience</option>
                                                                    <option value="landlords">Home Owners</option>
                                                                    <option value="tenants">Tenants</option>
                                                                    <option value="individuals">Individuals</option>
                                                                    <option value="division">By Block / Division</option>
                                                                    <option value="all">All People</option>
                                                                </select>

                                                                {targetAudience === 'landlords' && (
                                                                    <div className="mt-3">
                                                                        <label className="form-label">Home Owner Type</label>
                                                                        <div>
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="landlordType"
                                                                                    value="all"
                                                                                    checked={landlordType === 'all'}
                                                                                    onChange={(e) => setLandlordType(e.target.value)}
                                                                                />
                                                                                <label className="form-check-label">All Home Owners</label>
                                                                            </div>
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="landlordType"
                                                                                    value="resident"
                                                                                    checked={landlordType === 'resident'}
                                                                                    onChange={(e) => setLandlordType(e.target.value)}
                                                                                />
                                                                                <label className="form-check-label">Resident Home Owners</label>
                                                                            </div>
                                                                            <div className="form-check form-check-inline">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="radio"
                                                                                    name="landlordType"
                                                                                    value="non_resident"
                                                                                    checked={landlordType === 'non_resident'}
                                                                                    onChange={(e) => setLandlordType(e.target.value)}
                                                                                />
                                                                                <label className="form-check-label">Non-Resident Home Owners</label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Individual user selector - unchanged */}
                                                                {targetAudience === 'individuals' && (
                                                                    <div className="mt-3">
                                                                        <label className="form-label">Select User</label>
                                                                        <Select
                                                                            options={customerOptions}
                                                                            value={selectedUser}
                                                                            onChange={setSelectedUser}
                                                                            placeholder="Search for a user..."
                                                                            isClearable
                                                                            styles={{
                                                                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                                menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                                                                            }}
                                                                            menuPortalTarget={document.body}
                                                                        />
                                                                    </div>
                                                                )}

                                                                {/* Division multi-select */}
                                                                {targetAudience === 'division' && (
                                                                    <div className="mt-3">
                                                                        <label className="form-label">Select Block(s) / Division(s)</label>
                                                                        {divisions.length === 0 ? (
                                                                            <p className="text-muted small">Loading divisions...</p>
                                                                        ) : (
                                                                            <Select
                                                                                options={divisions.map(d => ({ value: d, label: d }))}
                                                                                value={selectedDivisions}
                                                                                onChange={setSelectedDivisions}
                                                                                isMulti
                                                                                placeholder="Select one or more blocks..."
                                                                                styles={{
                                                                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                                                    menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                                                                                }}
                                                                                menuPortalTarget={document.body}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="mb-3">
                                                                <label className="form-label">Marketing Channels</label>
                                                                <div>
                                                                    {['SMS', 'WhatsApp', 'Email'].map((channel) => (
                                                                        <div key={channel} className="form-check form-check-inline">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                value={channel}
                                                                                checked={channels.includes(channel)}
                                                                                onChange={() => handleChannelChange(channel)}
                                                                            />
                                                                            <label className="form-check-label">{channel}</label>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>

                                                            {channels.includes('Email') && (
                                                                <div className="mb-3">
                                                                    <label className="form-label">Email Subject</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control"
                                                                        name="emailSubject"
                                                                        value={emailSubject}
                                                                        onChange={(e) => setEmailSubject(e.target.value)}
                                                                        placeholder="Enter email subject..."
                                                                        required
                                                                    />
                                                                </div>
                                                            )}

                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    Message <small className="text-muted">(max {maxLength} characters)</small>
                                                                </label>

                                                                <ReactQuill
                                                                    ref={quillRef}
                                                                    theme="snow"
                                                                    value={message}
                                                                    onChange={(content, delta, source, editor) => {
                                                                        const plainText = editor.getText(); // real text, \u00A0 for nbsp, correctly 1 char per space
                                                                        if (plainText.length - 1 <= maxLength) {
                                                                            setMessage(content);
                                                                        }
                                                                    }}
                                                                    modules={{
                                                                        toolbar: [
                                                                            ['bold', 'italic', 'underline'],
                                                                            [{ list: 'ordered' }, { list: 'bullet' }],
                                                                            ['link'],
                                                                            ['clean']
                                                                        ]
                                                                    }}
                                                                    style={{ height: '200px', marginBottom: '42px' }}
                                                                />

                                                                <div className="text-end mt-1">
                                                                    <small className={getPlainTextLength() === maxLength ? 'text-danger' : 'text-muted'}>
                                                                        {getPlainTextLength()}/{maxLength}
                                                                    </small>
                                                                </div>
                                                            </div>

                                                            <div className='text-md-end'>
                                                                <button
                                                                    type="submit"
                                                                    className="btn btn-primary"
                                                                    onClick={handleSubmit}
                                                                    disabled={isSubmitting}
                                                                >
                                                                    {isSubmitting
                                                                        ? 'Sending Campaign...'
                                                                        : campaignType === 'immediate'
                                                                            ? 'Send Campaign'
                                                                            : 'Schedule Campaign'}
                                                                </button>
                                                            </div>
                                                        </div>

                                                    </div>

                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>

                    </div>
                </div>


            </div>

        </Layout>
    );
};

export default AddCampaign;
